/* eslint-disable no-restricted-imports */
import React, {useRef, useState, ReactNode} from 'react'
import {
    FloatingArrow,
    useClick,
    useFloating,
    useInteractions,
    arrow,
    offset,
} from '@floating-ui/react'
import {UIGameHelpCostsInformation} from './UIGameHelpCostsInformation'
import {UIGameHelpVPInformation} from './UIGameHelpVPInformation'
import styles from './UIGameHelpInformationButton.module.scss'
import {LobbyImage, ModeSettingEnum} from '@colonist/types'
import {getLobbyImagePath} from '../../../../../../code/client/shared/scripts/ui_utils/Utils'
import {T} from '../../../common/T'
import clsx from 'clsx'
import {PlayerColor} from '@colonist/game-states'
import {TranslationObject} from '@colonist/i18n'
import IconInfo from '@static/images/icon_info.svg'

export interface UIGameHelpInformationButtonProps {
    className?: string
    gameMode: ModeSettingEnum
    isMechanicIslandExplorationActive: boolean
    playerColor: PlayerColor
    victoryPointsToWin: number
}

type InformationTab = 'vp' | 'costs'

export const UIGameHelpInformationButton = ({
    gameMode,
    isMechanicIslandExplorationActive,
    playerColor,
    victoryPointsToWin,
    className,
}: UIGameHelpInformationButtonProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [activeTab, setActiveTab] = useState<InformationTab>('vp')
    const arrowRef = useRef(null)

    const {refs, floatingStyles, context} = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'bottom-start',
        middleware: [
            offset(12),
            arrow({element: arrowRef}),
        ],
    })

    const click = useClick(context)
    const {getReferenceProps, getFloatingProps} = useInteractions([click])

    const handleSwitchTab = () => {
        setActiveTab((prevTab) => prevTab === 'vp' ? 'costs' : 'vp')
    }

    const tabs: {
        [key: string]: {
            component: ReactNode
            title: TranslationObject
        }
    } = {
        vp: {
            component: <UIGameHelpVPInformation
                gameMode={gameMode}
                isMechanicIslandExplorationActive={isMechanicIslandExplorationActive}
                playerColor={playerColor}
                victoryPointsToWin={victoryPointsToWin}/>,
            title: {key: 'strings:game.endGame.icons.trophy'},
        },
        costs: {
            component: <UIGameHelpCostsInformation gameMode={gameMode} playerColor={playerColor}/>,
            title: {key: 'strings:game.buildCosts.title'},
        },
    }

    return (
        <div className={clsx(styles.container, className)}>
            <img
                className={styles.trigger}
                onClick={() => setIsOpen(!isOpen)}
                ref={refs.setReference}
                src={IconInfo}
                {...getReferenceProps()}
            />
            {isOpen && (
                <div
                    {...getFloatingProps()}
                    ref={refs.setFloating}
                    style={floatingStyles}
                >
                    <FloatingArrow className={styles.arrow} context={context} ref={arrowRef}/>
                    <div className={styles.dialogOverlay}>
                        <div className={styles.header}>
                            <div className={styles.heading}>
                                <T id={tabs[activeTab].title}/>
                            </div>
                            <div className={styles.tabSwitcher}>
                                <div className={clsx(styles.tabButton, styles.left)} onClick={handleSwitchTab}>
                                    <img
                                        className={styles.icon}
                                        src={getLobbyImagePath(LobbyImage.DownArrowBlack)}
                                    />
                                </div>
                                <div className={clsx(styles.tabButton, styles.right)} onClick={handleSwitchTab}>
                                    <img
                                        className={styles.icon}
                                        src={getLobbyImagePath(LobbyImage.DownArrowBlack)}
                                    />
                                </div>
                            </div>
                        </div>
                        {tabs[activeTab].component}
                    </div>
                </div>
            )}
        </div>
    )
}
